import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DataAssetSourceService } from '../services/data-asset-source.service';

/**
 * The current service contract guarantees a message. Count properties are
 * optional so the dialog can also display richer responses if the backend
 * returns them in the future.
 */
export interface ImportDataAssetsByAssetSourceResponse {
  inserted: number;
  updated: number;
  unchanged: number;
  markedDeleted: number;
  restored: number;
  hasCounts: boolean;
  message: string;
}

interface ImportSummaryItem {
  icon: string;
  text: string;
  kind: 'changed' | 'unchanged';
}

interface RawImportResponse {
  inserted?: unknown;
  updated?: unknown;
  unchanged?: unknown;
  markedDeleted?: unknown;
  restored?: unknown;
  message?: unknown;
}

@Component({
  selector: 'app-data-assets-capture-for-asset-source',
  templateUrl: './data-assets-capture-for-asset-source.component.html',
  styleUrls: ['./data-assets-capture-for-asset-source.component.scss']
})
export class DataAssetsCaptureForAssetSourceComponent implements OnInit {
  loading = true;
  errorMessage = '';
  response: ImportDataAssetsByAssetSourceResponse | null = null;
  summaryItems: ImportSummaryItem[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA)
    readonly data: { assetSourceId: string; assetSourceName?: string },
    private readonly dialogRef: MatDialogRef<DataAssetsCaptureForAssetSourceComponent>,
    private readonly dataAssetSourceService: DataAssetSourceService
  ) {}

  ngOnInit(): void {
    const assetSourceId = String(this.data?.assetSourceId ?? '').trim();

    if (!assetSourceId) {
      this.finishWithError('Asset Source ID is missing. Unable to import data assets.');
      return;
    }

    // The current endpoint accepts only assetSourceId.
    this.dataAssetSourceService.ImportDataAssetsByAssetSource(assetSourceId).subscribe({
      next: result => {
        this.response = this.normalizeResult(result);
        this.summaryItems = this.buildSummary(this.response);
        this.loading = false;
      },
      error: error => {
        this.finishWithError(
          this.extractErrorMessage(error) || 'Unable to import data assets from this source.'
        );
      }
    });
  }

  /** True when the backend supplied one or more non-zero change counts. */
  get hasChanges(): boolean {
    if (!this.response) {
      return false;
    }

    return this.response.inserted > 0 ||
      this.response.updated > 0 ||
      this.response.markedDeleted > 0 ||
      this.response.restored > 0;
  }

  get title(): string {
    return this.response ? 'Import completed' : 'Import results';
  }

  close(): void {
    if (this.loading) {
      return;
    }

    // The request succeeded if response is present. The parent can refresh
    // the data-asset list even when the backend returns only { message }.
    this.dialogRef.close({ shouldViewDataAssets: !!this.response });
  }

  private normalizeResult(rawResult: unknown): ImportDataAssetsByAssetSourceResponse {
    const result: RawImportResponse =
      rawResult !== null && typeof rawResult === 'object'
        ? rawResult as RawImportResponse
        : {};

    const countKeys: Array<keyof Pick<RawImportResponse,
      'inserted' | 'updated' | 'unchanged' | 'markedDeleted' | 'restored'>> = [
        'inserted', 'updated', 'unchanged', 'markedDeleted', 'restored'
      ];
    const hasCounts = countKeys.some(key => result[key] !== undefined && result[key] !== null);

    return {
      inserted: this.toCount(result.inserted),
      updated: this.toCount(result.updated),
      unchanged: this.toCount(result.unchanged),
      markedDeleted: this.toCount(result.markedDeleted),
      restored: this.toCount(result.restored),
      hasCounts,
      message: String(result.message ?? '')
    };
  }

  private buildSummary(result: ImportDataAssetsByAssetSourceResponse): ImportSummaryItem[] {
    const items: ImportSummaryItem[] = [];

    if (result.inserted > 0) {
      items.push({
        icon: 'add_circle',
        text: `${result.inserted} new ${this.pluralize(result.inserted, 'data asset')} inserted.`,
        kind: 'changed'
      });
    }
    if (result.updated > 0) {
      items.push({
        icon: 'edit',
        text: `${result.updated} existing ${this.pluralize(result.updated, 'data asset')} updated.`,
        kind: 'changed'
      });
    }
    if (result.markedDeleted > 0) {
      items.push({
        icon: 'delete_outline',
        text: `${result.markedDeleted} ${this.pluralize(result.markedDeleted, 'data asset')} marked as deleted.`,
        kind: 'changed'
      });
    }
    if (result.restored > 0) {
      items.push({
        icon: 'restore',
        text: `${result.restored} previously deleted ${this.pluralize(result.restored, 'data asset')} restored.`,
        kind: 'changed'
      });
    }
    if (result.unchanged > 0) {
      items.push({
        icon: 'check_circle_outline',
        text: `${result.unchanged} ${this.pluralize(result.unchanged, 'data asset')} unchanged.`,
        kind: 'unchanged'
      });
    }

    if (items.length === 0 && result.message) {
      items.push({ icon: 'info_outline', text: result.message, kind: 'unchanged' });
    }

    if (items.length === 0) {
      items.push({
        icon: 'info_outline',
        text: result.hasCounts
          ? 'Import completed. The server did not report any data-asset changes.'
          : 'The data-asset import request completed successfully.',
        kind: 'unchanged'
      });
    }

    return items;
  }

  private pluralize(count: number, singular: string): string {
    return count === 1 ? singular : `${singular}s`;
  }

  private toCount(value: unknown): number {
    const count = Number(value ?? 0);
    return Number.isFinite(count) && count > 0 ? Math.floor(count) : 0;
  }

  private finishWithError(message: string): void {
    this.errorMessage = message;
    this.loading = false;
  }

  private extractErrorMessage(error: unknown): string {
    if (!error || typeof error !== 'object') {
      return '';
    }

    const candidate = error as {
      error?: { message?: unknown } | string;
      message?: unknown;
    };
    const nestedMessage = candidate.error && typeof candidate.error === 'object'
      ? candidate.error.message
      : candidate.error;

    return String(nestedMessage ?? candidate.message ?? '').trim();
  }
}
